'use client';

import React, { useState, useEffect, useCallback } from 'react';
import { Toaster, toast } from 'sonner';
import Icon from '@/components/ui/AppIcon';
import { createClient } from '@/lib/supabase/client';

// Create supabase client once outside the component to avoid infinite re-render
const supabase = createClient();

interface MacAddress {
  id: string;
  mac_address: string;
  lista: 'whitelist' | 'blacklist' | 'sconosciuto';
  hostname: string;
  vendor: string;
  ip_associato: string;
  device_id: string | null;
  ultima_vista: string;
  notifica_inviata: boolean;
  note: string;
  created_at: string;
}

const listaColors: Record<string, string> = {
  whitelist: 'bg-green-500/20 text-green-400 border-green-500/30',
  blacklist: 'bg-red-500/20 text-red-400 border-red-500/30',
  sconosciuto: 'bg-amber-500/20 text-amber-400 border-amber-500/30',
};

const listaLabels: Record<string, string> = {
  whitelist: 'Whitelist',
  blacklist: 'Blacklist',
  sconosciuto: 'Sconosciuto',
};

type MacLista = 'whitelist' | 'blacklist' | 'sconosciuto';

const emptyForm: { mac_address: string; lista: MacLista; hostname: string; vendor: string; ip_associato: string; note: string } = {
  mac_address: '', lista: 'whitelist', hostname: '', vendor: '', ip_associato: '', note: '',
};

export default function MacManagementContent() {
  const [macs, setMacs] = useState<MacAddress[]>([]);
  const [loading, setLoading] = useState(true);
  const [listaFilter, setListaFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingMac, setEditingMac] = useState<MacAddress | null>(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);

  const fetchMacs = useCallback(async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase.from('mac_addresses').select('*').order('created_at', { ascending: false });
      if (error) throw error;
      setMacs(data || []);
    } catch (err: any) {
      toast.error('Errore caricamento MAC: ' + err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchMacs(); }, [fetchMacs]);

  const filtered = macs.filter(m => {
    const matchLista = listaFilter === 'all' || m.lista === listaFilter;
    const matchSearch = !search || m.mac_address.toLowerCase().includes(search.toLowerCase()) || m.hostname.toLowerCase().includes(search.toLowerCase()) || m.ip_associato.includes(search);
    return matchLista && matchSearch;
  });

  const openCreate = () => { setEditingMac(null); setForm(emptyForm); setShowModal(true); };
  const openEdit = (m: MacAddress) => {
    setEditingMac(m);
    setForm({ mac_address: m.mac_address, lista: m.lista, hostname: m.hostname, vendor: m.vendor, ip_associato: m.ip_associato, note: m.note });
    setShowModal(true);
  };

  const handleSave = async () => {
    if (!form.mac_address) { toast.error('MAC address obbligatorio'); return; }
    setSaving(true);
    try {
      if (editingMac) {
        const { error } = await supabase.from('mac_addresses').update(form).eq('id', editingMac.id);
        if (error) throw error;
        toast.success('MAC aggiornato');
      } else {
        const { error } = await supabase.from('mac_addresses').insert(form);
        if (error) throw error;
        toast.success('MAC aggiunto');
      }
      setShowModal(false);
      fetchMacs();
    } catch (err: any) {
      toast.error('Errore: ' + err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id: string, mac: string) => {
    if (!confirm(`Eliminare il MAC "${mac}"?`)) return;
    try {
      const { error } = await supabase.from('mac_addresses').delete().eq('id', id);
      if (error) throw error;
      toast.success('MAC eliminato');
      fetchMacs();
    } catch (err: any) {
      toast.error('Errore: ' + err.message);
    }
  };

  const handleMoveLista = async (id: string, lista: 'whitelist' | 'blacklist' | 'sconosciuto') => {
    try {
      const { error } = await supabase.from('mac_addresses').update({ lista }).eq('id', id);
      if (error) throw error;
      toast.success(`Spostato in ${listaLabels[lista]}`);
      fetchMacs();
    } catch (err: any) {
      toast.error('Errore: ' + err.message);
    }
  };

  const counts = {
    whitelist: macs.filter(m => m.lista === 'whitelist').length,
    blacklist: macs.filter(m => m.lista === 'blacklist').length,
    sconosciuto: macs.filter(m => m.lista === 'sconosciuto').length,
  };

  return (
    <div className="flex flex-col h-full bg-background">
      <Toaster position="bottom-right" theme="dark" richColors />

      <div className="px-6 py-5 border-b border-border bg-surface flex-shrink-0">
        <div className="flex items-center justify-between gap-4 flex-wrap">
          <div>
            <h1 className="text-2xl font-bold text-white">Gestione MAC Address</h1>
            <p className="text-sm text-secondary-color mt-0.5">Whitelist, blacklist e notifiche MAC sconosciuti</p>
          </div>
          <button onClick={openCreate} className="flex items-center gap-2 px-4 py-2 bg-sky-500 text-white rounded-lg text-sm font-semibold hover:bg-sky-400 transition-all">
            <Icon name="PlusIcon" size={16} />
            Aggiungi MAC
          </button>
        </div>

        <div className="grid grid-cols-3 gap-3 mt-4">
          {[
            { key: 'whitelist', label: 'Whitelist', color: 'text-green-400', bg: 'bg-green-500/10 border-green-500/20' },
            { key: 'blacklist', label: 'Blacklist', color: 'text-red-400', bg: 'bg-red-500/10 border-red-500/20' },
            { key: 'sconosciuto', label: 'Sconosciuti', color: 'text-amber-400', bg: 'bg-amber-500/10 border-amber-500/20' },
          ].map(s => (
            <div key={s.key} className={`rounded-lg border px-4 py-3 ${s.bg}`}>
              <p className={`text-xs font-bold uppercase tracking-wide ${s.color} opacity-70`}>{s.label}</p>
              <p className={`text-2xl font-bold mt-0.5 ${s.color}`}>{counts[s.key as keyof typeof counts]}</p>
            </div>
          ))}
        </div>
      </div>

      <div className="px-6 py-3 border-b border-border bg-surface flex items-center gap-3 flex-wrap">
        <div className="relative flex-1 min-w-48">
          <Icon name="MagnifyingGlassIcon" size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-color" />
          <input type="text" value={search} onChange={e => setSearch(e.target.value)} placeholder="Cerca MAC, hostname, IP..." className="w-full bg-surface-elevated border border-border text-white text-sm rounded-lg pl-9 pr-3 py-2 focus:outline-none focus:border-sky-500/50" />
        </div>
        <div className="flex bg-surface-elevated border border-border rounded-lg p-0.5">
          {(['all', 'whitelist', 'blacklist', 'sconosciuto'] as const).map(l => (
            <button key={l} onClick={() => setListaFilter(l)} className={`px-3 py-1.5 text-xs font-bold rounded-md transition-all capitalize ${listaFilter === l ? 'bg-sky-500/20 text-sky-400' : 'text-muted-color hover:text-secondary-color'}`}>
              {l === 'all' ? 'Tutti' : listaLabels[l]}
            </button>
          ))}
        </div>
        <span className="text-sm text-muted-color font-semibold ml-auto">{filtered.length} MAC</span>
      </div>

      <div className="flex-1 overflow-auto scrollbar-thin">
        {loading ? (
          <div className="flex items-center justify-center h-64">
            <div className="w-8 h-8 border-2 border-sky-500 border-t-transparent rounded-full animate-spin" />
          </div>
        ) : filtered.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-64 gap-3">
            <Icon name="FingerPrintIcon" size={40} className="text-muted-color" />
            <p className="text-secondary-color font-semibold">Nessun MAC trovato</p>
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead className="sticky top-0 bg-surface border-b border-border z-10">
              <tr>
                {['MAC Address', 'Lista', 'Hostname', 'Vendor', 'IP Associato', 'Ultima Vista', 'Notifica', 'Azioni'].map(h => (
                  <th key={h} className="text-left px-4 py-3 text-xs font-bold text-muted-color uppercase tracking-wider whitespace-nowrap">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {filtered.map(m => (
                <tr key={m.id} className="hover:bg-surface-elevated/30 transition-colors">
                  <td className="px-4 py-3 font-mono text-white font-semibold">{m.mac_address}</td>
                  <td className="px-4 py-3">
                    <span className={`inline-flex items-center gap-1.5 px-2 py-1 rounded-full text-xs font-bold border ${listaColors[m.lista]}`}>
                      <span className="w-1.5 h-1.5 rounded-full bg-current" />
                      {listaLabels[m.lista]}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-secondary-color">{m.hostname || '—'}</td>
                  <td className="px-4 py-3 text-secondary-color text-xs">{m.vendor || '—'}</td>
                  <td className="px-4 py-3 font-mono text-secondary-color text-xs">{m.ip_associato || '—'}</td>
                  <td className="px-4 py-3 text-secondary-color text-xs">{m.ultima_vista ? new Date(m.ultima_vista).toLocaleString('it-IT') : '—'}</td>
                  <td className="px-4 py-3">
                    {m.notifica_inviata ? (
                      <span className="text-xs text-amber-400 font-semibold">⚠ Inviata</span>
                    ) : (
                      <span className="text-xs text-muted-color">—</span>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-1">
                      {m.lista !== 'whitelist' && (
                        <button onClick={() => handleMoveLista(m.id, 'whitelist')} className="p-1.5 rounded-lg hover:bg-green-500/10 text-muted-color hover:text-green-400 transition-colors" title="Sposta in Whitelist">
                          <Icon name="CheckCircleIcon" size={14} />
                        </button>
                      )}
                      {m.lista !== 'blacklist' && (
                        <button onClick={() => handleMoveLista(m.id, 'blacklist')} className="p-1.5 rounded-lg hover:bg-red-500/10 text-muted-color hover:text-red-400 transition-colors" title="Sposta in Blacklist">
                          <Icon name="NoSymbolIcon" size={14} />
                        </button>
                      )}
                      <button onClick={() => openEdit(m)} className="p-1.5 rounded-lg hover:bg-surface-elevated text-muted-color hover:text-white transition-colors">
                        <Icon name="PencilIcon" size={14} />
                      </button>
                      <button onClick={() => handleDelete(m.id, m.mac_address)} className="p-1.5 rounded-lg hover:bg-red-500/10 text-muted-color hover:text-red-400 transition-colors">
                        <Icon name="TrashIcon" size={14} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {showModal && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-surface border border-border rounded-2xl w-full max-w-lg shadow-2xl">
            <div className="flex items-center justify-between p-5 border-b border-border">
              <h2 className="text-lg font-bold text-white">{editingMac ? 'Modifica MAC' : 'Aggiungi MAC'}</h2>
              <button onClick={() => setShowModal(false)} className="p-1.5 rounded-lg hover:bg-surface-elevated transition-colors">
                <Icon name="XMarkIcon" size={18} className="text-muted-color" />
              </button>
            </div>
            <div className="p-5 space-y-4">
              <div>
                <label className="block text-sm font-semibold text-secondary-color mb-1.5">MAC Address *</label>
                <input type="text" value={form.mac_address} onChange={e => setForm({ ...form, mac_address: e.target.value })} className="w-full bg-surface-elevated border border-border text-white text-sm rounded-lg px-3 py-2.5 focus:outline-none focus:border-sky-500/50 font-mono" placeholder="AA:BB:CC:DD:EE:FF" />
              </div>
              <div>
                <label className="block text-sm font-semibold text-secondary-color mb-1.5">Lista</label>
                <select value={form.lista} onChange={e => setForm({ ...form, lista: e.target.value as any })} className="w-full bg-surface-elevated border border-border text-white text-sm rounded-lg px-3 py-2.5 focus:outline-none focus:border-sky-500/50">
                  <option value="whitelist">Whitelist</option>
                  <option value="blacklist">Blacklist</option>
                  <option value="sconosciuto">Sconosciuto</option>
                </select>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-semibold text-secondary-color mb-1.5">Hostname</label>
                  <input type="text" value={form.hostname} onChange={e => setForm({ ...form, hostname: e.target.value })} className="w-full bg-surface-elevated border border-border text-white text-sm rounded-lg px-3 py-2.5 focus:outline-none focus:border-sky-500/50" />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-secondary-color mb-1.5">IP Associato</label>
                  <input type="text" value={form.ip_associato} onChange={e => setForm({ ...form, ip_associato: e.target.value })} className="w-full bg-surface-elevated border border-border text-white text-sm rounded-lg px-3 py-2.5 focus:outline-none focus:border-sky-500/50 font-mono" />
                </div>
              </div>
              <div>
                <label className="block text-sm font-semibold text-secondary-color mb-1.5">Vendor</label>
                <input type="text" value={form.vendor} onChange={e => setForm({ ...form, vendor: e.target.value })} className="w-full bg-surface-elevated border border-border text-white text-sm rounded-lg px-3 py-2.5 focus:outline-none focus:border-sky-500/50" />
              </div>
              <div>
                <label className="block text-sm font-semibold text-secondary-color mb-1.5">Note</label>
                <textarea value={form.note} onChange={e => setForm({ ...form, note: e.target.value })} rows={2} className="w-full bg-surface-elevated border border-border text-white text-sm rounded-lg px-3 py-2.5 focus:outline-none focus:border-sky-500/50 resize-none" />
              </div>
            </div>
            <div className="flex gap-3 p-5 border-t border-border">
              <button onClick={() => setShowModal(false)} className="flex-1 px-4 py-2.5 border border-border text-secondary-color rounded-lg text-sm font-semibold hover:bg-surface-elevated transition-all">Annulla</button>
              <button onClick={handleSave} disabled={saving} className="flex-1 px-4 py-2.5 bg-sky-500 text-white rounded-lg text-sm font-semibold hover:bg-sky-400 transition-all disabled:opacity-50">
                {saving ? 'Salvataggio...' : 'Salva'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
